import React, { useRef, useState } from "react";
import { Upload, Loader2, Link as LinkIcon, X } from "lucide-react";
import { api, resolveUrl } from "../../lib/api";
import { toast } from "sonner";

export default function ImageUpload({ value, onChange, label = "Image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("url"); // "url" | "upload"

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => setMode("url")}
          className={`px-2 py-1 text-[10px] tracking-[0.15em] uppercase border ${
            mode === "url" ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-600"
          }`}
        >
          <LinkIcon className="w-3 h-3 inline mr-1" /> URL
        </button>
        <button
          type="button"
          onClick={() => setMode("upload")}
          className={`px-2 py-1 text-[10px] tracking-[0.15em] uppercase border ${
            mode === "upload" ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-600"
          }`}
        >
          <Upload className="w-3 h-3 inline mr-1" /> Upload
        </button>
      </div>

      {mode === "url" ? (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste image URL or leave blank"
          className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm"
        />
      ) : (
        <div className="border-2 border-dashed border-gray-300 p-4 text-center">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F3864] text-white text-sm disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "Uploading..." : "Choose Image"}
          </button>
          <div className="text-xs text-gray-500 mt-2">JPG, PNG, GIF, WEBP · Max 10MB</div>
        </div>
      )}

      {value && (
        <div className="mt-3 relative inline-block">
          <img src={resolveUrl(value)} alt="preview" className="h-24 border border-gray-200" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 w-6 h-6 bg-[#C00000] text-white flex items-center justify-center rounded-full"
            title="Clear"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
